import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Info,
    DollarSign,
    ShieldCheck,
    Building2,
    Globe,
    FileText,
    Users,
    UserPlus,
    Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Combobox } from '../../components/ui/combobox';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const CATEGORY_OPTIONS = [
    'Core Software',
    'Online Portal',
];

const STATUS_OPTIONS = ['Active', 'Inactive', 'Archived'];
const TWO_FA_OPTIONS = ['Enabled', 'Disabled', 'N/A'];
const PAYMENT_METHOD_OPTIONS = ['Card', 'Direct EFT', 'Invoice', 'Other'];
const PURCHASE_MODE_OPTIONS = ['Monthly', 'Annual', 'One Time', 'Free'];
const COST_MODEL_OPTIONS = [
    'Per user per month',
    'Per user per year',
    'Fixed fee per month',
    'Fixed fee per year',
];

const CreateBusinessSystem: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [assignments, setAssignments] = useState<{ userId: string, role: string }[]>([]);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    React.useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setInitialDataLoading(true);
                const [usersRes, deptsRes] = await Promise.all([
                    api.get('/api/users?all_users=true'),
                    api.get('/api/departments')
                ]);

                if (usersRes.success) setUsers(usersRes.users);
                if (deptsRes.success) setDepartments(deptsRes.departments);

            } catch (err) {
                console.error('Error fetching initial data:', err);
                toast.error('Failed to load required data');
            } finally {
                setInitialDataLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        department: '',
        status: 'Active',
        purchaseMode: '',
        monthlyCost: '',
        costModel: '',
        paymentMethod: '',
        paymentDetails: '',
        totalLicenses: '',
        usedLicenses: '',
        twoFactorAuth: 'N/A',
        vendor: '',
        website: '',
        supportEmail: '',
        contactPhone: '',
        contactName: '',
        contractNumber: '',
        renewalDate: '',
        notes: '',
    });

    if (!ability.can(ACTIONS.CREATE, RESOURCES.BUSINESS_SYSTEM)) {
        return <AccessRestricted />;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: Record<string, boolean> = {};
        if (!formData.name.trim()) newErrors.name = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);

            const isPerUser = formData.costModel === 'Per user per month' || formData.costModel === 'Per user per year';
            let costPerLicense = null;
            if (isPerUser && formData.monthlyCost && formData.totalLicenses) {
                const monthlyCost = parseFloat(formData.monthlyCost);
                const totalLicenses = parseInt(formData.totalLicenses);
                costPerLicense = formData.costModel === 'Per user per year'
                    ? (monthlyCost * 12) / totalLicenses
                    : monthlyCost / totalLicenses;
            }

            const payload = {
                ...formData,
                monthlyCost: formData.monthlyCost ? parseFloat(formData.monthlyCost) : null,
                totalLicenses: formData.totalLicenses ? parseInt(formData.totalLicenses) : 0,
                usedLicenses: formData.usedLicenses ? parseInt(formData.usedLicenses) : 0,
                renewalDate: formData.renewalDate || null,
                costPerLicense: costPerLicense ? parseFloat(costPerLicense.toFixed(2)) : null,
                assignments: assignments.filter(a => a.userId), // Send only valid assignments
            };

            await api.post('/api/business-systems', payload, { skipErrorToast: true });
            toast.success('Business system created successfully');
            navigate('/business-systems');
        } catch (err: any) {
            toast.error(err.message || 'Failed to create business system');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/business-systems')}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Add Business System</h1>
                    <p className="text-gray-500 font-medium text-sm">Register a new software or business system</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Form Area */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Basic Information */}
                        <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                    <Info size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Basic Information</h3>
                                    <p className="text-gray-500 text-sm font-medium">Essential details about the system</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">System Name *</label>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Microsoft 365, BambooHR"
                                        className={`rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium ${errors.name ? "border-red-500 ring-1 ring-red-500" : ""}`}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Category</label>
                                    <Select value={formData.category} onValueChange={(val) => handleSelectChange('category', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select Category" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {CATEGORY_OPTIONS.map(cat => (
                                                <SelectItem key={cat} value={cat} className="font-bold py-3">{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Department</label>
                                    <Combobox
                                        options={Array.from(new Map(departments.map(d => [d.departmentName, d])).values()).map(d => ({
                                            label: d.departmentName,
                                            value: d.departmentName // Store name as per current schema
                                        }))}
                                        value={formData.department}
                                        onChange={(val) => handleSelectChange('department', val || '')}
                                        placeholder="Select Department"
                                        searchPlaceholder="Search departments..."
                                        emptyMessage="No departments found."
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Purchase Mode</label>
                                    <Select value={formData.purchaseMode} onValueChange={(val) => handleSelectChange('purchaseMode', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select Purchase Mode" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {PURCHASE_MODE_OPTIONS.map(p => (
                                                <SelectItem key={p} value={p} className="font-bold py-3">{p}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Description</label>
                                <Textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Describe what this system is used for..."
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[120px] focus:bg-white transition-all font-medium"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Status</label>
                                    <Select value={formData.status} onValueChange={(val) => handleSelectChange('status', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select Status" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {STATUS_OPTIONS.map(s => (
                                                <SelectItem key={s} value={s} className="font-bold py-3">{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">2FA Status</label>
                                    <Select value={formData.twoFactorAuth} onValueChange={(val) => handleSelectChange('twoFactorAuth', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select 2FA Status" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {TWO_FA_OPTIONS.map(o => (
                                                <SelectItem key={o} value={o} className="font-bold py-3">{o}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </section>

                        {/* Cost & Licensing */}
                        <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-2 bg-green-50 rounded-xl text-green-600">
                                    <DollarSign size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Cost & Licensing</h3>
                                    <p className="text-gray-500 text-sm font-medium">Financial and license allocation details</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Monthly Cost</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                                        <Input
                                            name="monthlyCost"
                                            type="number"
                                            step="0.01"
                                            value={formData.monthlyCost}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-8"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Cost Model</label>
                                    <Select value={formData.costModel} onValueChange={(val) => handleSelectChange('costModel', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select Cost Model" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {COST_MODEL_OPTIONS.map(m => (
                                                <SelectItem key={m} value={m} className="font-bold py-3">{m}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Payment Method</label>
                                    <Select value={formData.paymentMethod} onValueChange={(val) => handleSelectChange('paymentMethod', val)}>
                                        <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                            <SelectValue placeholder="Select Payment Method" />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-2xl border-gray-100">
                                            {PAYMENT_METHOD_OPTIONS.map(m => (
                                                <SelectItem key={m} value={m} className="font-bold py-3">{m}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Payment Details</label>
                                    <Input
                                        name="paymentDetails"
                                        value={formData.paymentDetails}
                                        onChange={handleInputChange}
                                        placeholder="e.g., **** 5678"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Total Licenses</label>
                                    <Input
                                        name="totalLicenses"
                                        type="number"
                                        min="0"
                                        value={formData.totalLicenses}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Used Licenses</label>
                                    <Input
                                        name="usedLicenses"
                                        type="number"
                                        min="0"
                                        value={formData.usedLicenses}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                            </div>

                            {(formData.costModel === 'Per user per month' || formData.costModel === 'Per user per year') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Cost Per License</label>
                                        <div className="relative group">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                                            <Input
                                                type="text"
                                                readOnly
                                                value={formData.monthlyCost && formData.totalLicenses && parseInt(formData.totalLicenses) > 0
                                                    ? formData.costModel === 'Per user per year'
                                                        ? ((parseFloat(formData.monthlyCost) * 12) / parseInt(formData.totalLicenses)).toFixed(2)
                                                        : (parseFloat(formData.monthlyCost) / parseInt(formData.totalLicenses)).toFixed(2)
                                                    : '—'}
                                                className="rounded-2xl border-gray-100 bg-gray-100 h-12 font-medium pl-8 text-gray-600 cursor-default"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </section>

                        {/* Vendor Information */}
                        <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
                                    <Building2 size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Vendor Information</h3>
                                    <p className="text-gray-500 text-sm font-medium">Vendor and contract details</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Vendor</label>
                                    <Input
                                        name="vendor"
                                        value={formData.vendor}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Microsoft, Atlassian"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Website</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                            <Globe size={16} />
                                        </div>
                                        <Input
                                            name="website"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                            placeholder="https://..."
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-10"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Support Email</label>
                                    <Input
                                        name="supportEmail"
                                        type="email"
                                        value={formData.supportEmail}
                                        onChange={handleInputChange}
                                        placeholder="support@vendor.com"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Contract Number</label>
                                    <Input
                                        name="contractNumber"
                                        value={formData.contractNumber}
                                        onChange={handleInputChange}
                                        placeholder="e.g., CNT-2024-001"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Contact Name</label>
                                    <Input
                                        name="contactName"
                                        value={formData.contactName}
                                        onChange={handleInputChange}
                                        placeholder="e.g., Customer Success"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Contact Phone</label>
                                    <Input
                                        name="contactPhone"
                                        value={formData.contactPhone}
                                        onChange={handleInputChange}
                                        placeholder="e.g., +1-801-224-6727"
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Renewal Date</label>
                                    <Input
                                        type="date"
                                        name="renewalDate"
                                        max="9999-12-31"
                                        value={formData.renewalDate}
                                        onChange={handleInputChange}
                                        className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* User Assignments */}
                        <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6 text-slate-900">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-lg">Initial User Assignments</h3>
                                        <p className="text-gray-500 text-sm font-medium">Assign users who should have immediate access</p>
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAssignments([...assignments, { userId: '', role: 'User' }])}
                                    className="rounded-xl border-blue-100 text-blue-600 hover:bg-blue-50 font-bold gap-2"
                                >
                                    <UserPlus size={16} />
                                    Add User
                                </Button>
                            </div>

                            <div className="space-y-4">
                                {assignments.length === 0 ? (
                                    <div className="p-8 border-2 border-dashed border-gray-100 rounded-2xl text-center">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No users assigned yet</p>
                                    </div>
                                ) : (
                                    assignments.map((assignment, index) => (
                                        <div key={index} className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50/50 rounded-2xl border border-gray-100 relative group animate-in slide-in-from-left-2 duration-300">
                                            <div className="flex-1 space-y-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Select User</label>
                                                <Combobox
                                                    options={users.map(u => ({
                                                        label: `${u.name} (${u.jobRole || 'No Role'})`,
                                                        value: u.id.toString()
                                                    }))}
                                                    value={assignment.userId}
                                                    onChange={(val) => {
                                                        const newAssignments = [...assignments];
                                                        newAssignments[index].userId = val || '';
                                                        setAssignments(newAssignments);
                                                    }}
                                                    placeholder="Search users..."
                                                    searchPlaceholder="Search by name or role..."
                                                    emptyMessage="No users found."
                                                />
                                            </div>
                                            <div className="w-full md:w-[150px] space-y-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Role</label>
                                                <Input
                                                    value={assignment.role}
                                                    onChange={(e) => {
                                                        const newAssignments = [...assignments];
                                                        newAssignments[index].role = e.target.value;
                                                        setAssignments(newAssignments);
                                                    }}
                                                    placeholder="e.g. Admin, User"
                                                    className="rounded-xl border-gray-100 bg-white h-11 focus:bg-white transition-all font-bold text-slate-900"
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setAssignments(assignments.filter((_, i) => i !== index))}
                                                className="absolute -right-2 -top-2 md:relative md:right-0 md:top-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg h-11 w-11 shadow-sm md:shadow-none bg-white md:bg-transparent"
                                            >
                                                <Trash2 size={18} />
                                            </Button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-8">
                        {/* Quick Summary */}
                        <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6 sticky top-8">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-2 bg-amber-50 rounded-xl text-amber-600">
                                    <FileText size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Notes</h3>
                                    <p className="text-gray-500 text-sm font-medium">Additional notes or remarks</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Any additional notes about this system..."
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[150px] focus:bg-white transition-all font-medium"
                                />
                            </div>

                            {/* Security Summary */}
                            <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                                <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                                    <ShieldCheck size={16} className="text-blue-600" />
                                    Quick Summary
                                </div>
                                <div className="space-y-2 text-xs text-gray-500">
                                    <div className="flex justify-between">
                                        <span>System</span>
                                        <span className="font-bold text-gray-700">{formData.name || '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Category</span>
                                        <span className="font-bold text-gray-700">{formData.category || '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Status</span>
                                        <span className="font-bold text-gray-700">{formData.status || '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Monthly Cost</span>
                                        <span className="font-bold text-gray-700">{formData.monthlyCost ? `$${formData.monthlyCost}` : '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>2FA</span>
                                        <span className="font-bold text-gray-700">{formData.twoFactorAuth}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white border-0 shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98] h-12 rounded-2xl text-sm font-bold"
                            >
                                {loading ? 'Creating...' : 'Create Business System'}
                            </Button>

                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/business-systems')}
                                className="w-full h-12 rounded-2xl text-sm font-bold border-gray-100"
                            >
                                Cancel
                            </Button>
                        </section>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateBusinessSystem;
